import { useState } from 'react';
import { fmt1, fmtFecha, fmtInt } from '@/lib/format';
import { formatoDecimal } from '@/lib/liquidacion';

export interface NaveChartPoint {
  fecha: string;
  age: number;
  actual: number | null;
  reference: number | null;
  count?: number;
}
/** Selección nativa por fecha y tabla; los huecos no se conectan como registros cero. */
export function NaveEvolutionChart({
  points,
  title,
  unit,
  referenceLabel,
  weight = false,
  initialDate,
}: {
  points: NaveChartPoint[];
  title: string;
  unit: string;
  referenceLabel: string;
  weight?: boolean;
  initialDate?: string;
}) {
  const [selectedDate, select] = useState<string | null>(null);
  const index = Math.max(
    0,
    points.findIndex((p) => p.fecha === (selectedDate ?? initialDate ?? points.at(-1)?.fecha)),
  );
  const selected = points[index];
  const display = (value: number | null, reference = false) =>
    value == null
      ? 'Sin registro'
      : `${weight ? formatoDecimal(value, 2) : reference ? fmt1(value) : fmtInt(value)} ${unit}`;
  if (!selected) return <p className="nave-caption">Sin datos para representar.</p>;
  const max = Math.max(1, ...points.flatMap((p) => [p.actual ?? 0, p.reference ?? 0])) * 1.1;
  const x = (i: number) => 40 + (i / Math.max(1, points.length - 1)) * 260;
  const y = (v: number) => 146 - (v / max) * 124;
  const path = (key: 'actual' | 'reference') => {
    let gap = true;
    return points
      .map((p, i) => {
        if (p[key] == null) {
          if (!weight || key !== 'actual') gap = true;
          return '';
        }
        const command = `${gap ? 'M' : 'L'}${x(i)} ${y(p[key])}`;
        gap = false;
        return command;
      })
      .join(' ');
  };
  return (
    <section className="nave-evolution" aria-label={title}>
      <h2>{title}</h2>
      <div className="nave-chart-legend" aria-hidden>
        <span>Actual</span>
        <span>{referenceLabel}</span>
      </div>
      <label className="nave-caption nave-chart-date" htmlFor="nave-chart-date">
        Consultar fecha
        <select
          id="nave-chart-date"
          className="ui-field"
          value={selected.fecha}
          onChange={(e) => select(e.target.value)}
        >
          {points.map((p) => (
            <option key={p.fecha} value={p.fecha}>
              {fmtFecha(p.fecha)} · Día {p.age}
            </option>
          ))}
        </select>
      </label>
      <div className="nave-chart-values" aria-live="polite">
        <div>
          <span>Actual</span>
          <strong>{display(selected.actual)}</strong>
          {weight &&
            selected.actual != null &&
            selected.reference != null &&
            selected.reference > 0 && (
              <small>
                {selected.actual >= selected.reference ? '+' : ''}
                {fmtInt((selected.actual - selected.reference) * 1000)} g ·{' '}
                {selected.actual >= selected.reference ? '+' : ''}
                {fmt1((selected.actual / selected.reference - 1) * 100)} % frente a la curva
              </small>
            )}
        </div>
        <div>
          <span>{referenceLabel}</span>
          <strong>{display(selected.reference, true)}</strong>
          {selected.count != null && <small>{selected.count} camadas con parte</small>}
        </div>
      </div>
      <svg viewBox="0 0 320 180" className="nave-chart" aria-hidden>
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <line x1={40} x2={300} y1={y(v)} y2={y(v)} stroke="var(--c-border)" />
            <text x={32} y={y(v) + 4} textAnchor="end">
              {fmt1(v)}
            </text>
          </g>
        ))}
        <path d={path('reference')} className="nave-chart-reference" />
        {points.map((p, i) =>
          p.reference == null ? null : (
            <circle
              key={`reference-${p.fecha}`}
              cx={x(i)}
              cy={y(p.reference)}
              r={points.length < 15 ? 2 : 1}
              fill="var(--c-text-secondary)"
            />
          ),
        )}
        <path d={path('actual')} className="nave-chart-actual" />
        {points.map((p, i) =>
          p.actual == null ? null : (
            <circle
              key={p.fecha}
              cx={x(i)}
              cy={y(p.actual)}
              r={points.length < 15 ? 3 : 1.5}
              className="nave-chart-dot"
            />
          ),
        )}
        <line
          x1={x(index)}
          x2={x(index)}
          y1={22}
          y2={146}
          stroke="var(--c-text-secondary)"
          opacity={0.4}
        />
        <text x={40} y={169}>
          Día {points[0].age}
        </text>
        <text x={300} y={169} textAnchor="end">
          Día {points.at(-1)!.age}
        </text>
      </svg>
      <details className="nave-disclosure">
        <summary>Ver valores por fecha</summary>
        <table className="nave-table">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Actual</th>
              <th>{referenceLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.fecha}>
                <th scope="row">{fmtFecha(p.fecha)}</th>
                <td>{display(p.actual)}</td>
                <td>
                  {display(p.reference, true)}
                  {p.count != null && ` (${p.count})`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}
